import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, OnChanges } from '@angular/core';
import { TimeService } from '../../../core/ctf/services/time.service';
import { Subject } from "rxjs";

@Component({
  selector: 'app-countdown-timer',
  templateUrl: './countdown-timer.component.html',
  styleUrls: ['./countdown-timer.component.scss']
})
export class CountdownTimerComponent implements OnInit, OnChanges, OnDestroy {
  formattedTime: string = '';
  progressPercentage: number = 100;
  showTimer: boolean = true;
  isInFuture: boolean = false;

  @Input() start: Date | null = null;
  @Input() end: Date | null = null;

  private onDestroy$ = new Subject<void>();

  constructor(
      private timeService: TimeService,
      private cdr: ChangeDetectorRef,
  ) {
    this.formattedTime = this.timeService.convertSecondsToBestUnit(0);
  }

  ngOnInit() {
    this.initTimer();
  }

  ngOnChanges() {
    this.initTimer();
  }

  /**
   * Inits the timer if start and end date have a value
   */
  initTimer() {
    if (this.start && this.end) {
      const now = this.timeService.nowUTC();

      if (this.end <= now) {
        this.showTimer = false; // Event has ended
        return;
      }

      if (this.start > now) {
        this.isInFuture = true; // Event hasn't started yet
        this.startFutureTimer();
      } else {
        this.isInFuture = false; // Event has started
        this.startTimer();
      }
    } else {
      this.showTimer = false;
    }
  }

  /**
    * Cleans up the component by stopping the timer when the component is destroyed.
    */
  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.stopTimer();
  }

  /**
   * Starts the countdown for an event that has already started.
   */
  private startTimer() {
    this.updateDisplay();
    setInterval(() => this.updateDisplay(), 1000);
  }

  /**
   * Starts the countdown until the event starts (if it's in the future).
   */
  private startFutureTimer() {
    this.updateFutureDisplay();
    setInterval(() => this.updateFutureDisplay(), 1000);
  }

  /**
   * Stops the countdown timer.
   */
  private stopTimer() {
    this.timeService.stopTimer();
  }

  /**
   * Updates the countdown display and progress bar for an event that is currently active.
   */
  private updateDisplay() {
    if (this.start && this.end) {
      const now = this.timeService.nowUTC();

      if (now >= this.end) {
        this.stopTimer();
        this.showTimer = false;
        return;
      }

      const totalDuration = this.end.getTime() - this.start.getTime();
      const remainingDuration = this.end.getTime() - now.getTime();

      this.progressPercentage = (remainingDuration / totalDuration) * 100;

      this.progressPercentage = Math.max(0, Math.min(100, this.progressPercentage));

      this.formattedTime = this.timeService.convertSecondsToBestUnit(remainingDuration / 1000);

      this.cdr.detectChanges();
    }
  }

  /**
   * Updates the display for the countdown until the event starts.
   */
  private updateFutureDisplay() {
    if (this.start) {
      const now = this.timeService.nowUTC();

      if (now >= this.start) {
        this.isInFuture = false;
        this.startTimer();
        return;
      }

      const remainingTime = this.start.getTime() - now.getTime();
      this.formattedTime = this.timeService.convertSecondsToBestUnit(remainingTime / 1000);
      this.progressPercentage = 100; // Full progress before the event starts
    }
  }
}
